import React, { useState, useEffect } from 'react';
import { AuthService } from '../services/authService';
import { ApiService, type GameRecord } from '../services/apiService';
import { eloNumberTitle, formatEloNumber } from '../utils/eloUtils';
import Sidebar from './Sidebar';
import GameReplay from './GameReplay';
import PageCloseButton from './PageCloseButton';
import './StatisticsPage.css';

interface StatisticsPageProps {
  user: {
    username: string;
    email: string;
  };
  onBack: () => void;
  onPlayClick: () => void;
  onHowToPlayClick: () => void;
  onAboutClick?: () => void;  // Made optional since modal doesn't need it
  onPlayersClick?: () => void;
  onStatsClick?: () => void;
  authState: {
    isAuthenticated: boolean;
    user: {
      username: string;
      email: string;
    } | null;
    isGuest: boolean;
  };
  onLogout: () => void;
  onSignInClick: () => void;
  onSignUpClick: () => void;
  isModal?: boolean;
  viewingUserId?: number; // Optional: if provided, show stats for this user instead of current user
  viewingUsername?: string; // Optional: username to display in title when viewing another user
}

interface Statistics {
  wins: number;
  losses: number;
  draws: number;
  totalGames: number;
  winRate: number;
  winPercentage: number;
  winStreak: number;
  wegosWon: number;
  wegosLost: number;
  // Color performance
  whiteWinRate: number;
  blackWinRate: number;
  gamesAsWhite: number;
  gamesAsBlack: number;
  // Streak stats
  longestWinStreak: number;
  longestLossStreak: number;
  consecutiveDays: number;
  // Time-based stats
  gamesThisWeek: number;
  gamesThisMonth: number;
  mostActiveDay: string;
  averageGamesPerDay: number;
  elo_fast?: number;
  elo_standard?: number;
  rated_games_fast?: number;
  rated_games_standard?: number;
}

// Helper function to format game timestamps in user's local timezone
// SQLite stores timestamps in UTC, so we need to parse them as UTC and convert to local
const formatGameTimestamp = (timestamp: string, format: 'date' | 'time' | 'datetime' = 'datetime'): string => {
  if (!timestamp) return '';
  
  // If timestamp doesn't have timezone info, assume it's UTC (SQLite format)
  // SQLite format: "YYYY-MM-DD HH:MM:SS"
  let date: Date;
  if (timestamp.includes('T') || timestamp.includes('Z') || timestamp.includes('+')) {
    // Already has timezone info (ISO format)
    date = new Date(timestamp);
  } else {
    // SQLite format without timezone - treat as UTC
    date = new Date(timestamp + ' UTC');
  }
  
  // Format based on requested format
  if (format === 'date') {
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  } else if (format === 'time') {
    return date.toLocaleTimeString(undefined, { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  } else {
    return date.toLocaleString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  }
};

const StatisticsPage: React.FC<StatisticsPageProps> = ({ 
  user, 
  onBack,
  onPlayClick,
  onHowToPlayClick,
  onAboutClick,
  onPlayersClick,
  onStatsClick,
  authState,
  onLogout,
  onSignInClick,
  onSignUpClick,
  isModal,
  viewingUserId,
  viewingUsername
}) => {
  const [stats, setStats] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [gameHistory, setGameHistory] = useState<GameRecord[]>([]);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [tournamentOnly, setTournamentOnly] = useState(false);
  const [selectedReplay, setSelectedReplay] = useState<{
    gameId: string;
    opponent: string;
    result: 'win' | 'loss' | 'draw';
    playerColor: 'white' | 'black';
    timestamp: string;
    winType?: 'igo' | 'wego' | null;
    gameEndReason?: string | null;
  } | null>(null);

  useEffect(() => {
    fetchStatistics();
  }, [viewingUserId]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch stats for the specified user or current user
      const { stats: userStats, error: statsError } = viewingUserId 
        ? await ApiService.getUserStatsById(viewingUserId)
        : await AuthService.getUserStats();
      
      if (statsError) {
        setError(statsError);
        setLoading(false);
        return;
      }

      if (userStats) {
        // Transform API response to match Statistics interface
        // Backend returns winRate, but component expects winPercentage
        const wins = userStats.wins || 0;
        const losses = userStats.losses || 0;
        const draws = userStats.draws || 0;
        const totalGames = userStats.totalGames || (wins + losses + draws);
        
        // Calculate winPercentage from winRate or calculate it
        const winRate = userStats.winRate || (totalGames > 0 ? (wins / totalGames) * 100 : 0);
        const winPercentage = typeof winRate === 'number' ? winRate : parseFloat(winRate) || 0;
        
        const transformedStats: Statistics = {
          wins,
          losses,
          draws,
          totalGames,
          winRate: winPercentage,
          winPercentage: winPercentage,
          winStreak: userStats.winStreak || userStats.currentStreak || 0,
          wegosWon: 0, // Will be calculated after game history loads
          wegosLost: 0, // Will be calculated after game history loads
          whiteWinRate: 0, // Will be calculated after game history loads
          blackWinRate: 0, // Will be calculated after game history loads
          gamesAsWhite: 0, // Will be calculated after game history loads
          gamesAsBlack: 0, // Will be calculated after game history loads
          longestWinStreak: 0, // Will be calculated after game history loads
          longestLossStreak: 0, // Will be calculated after game history loads
          consecutiveDays: 0, // Will be calculated after game history loads
          gamesThisWeek: 0, // Will be calculated after game history loads
          gamesThisMonth: 0, // Will be calculated after game history loads
          mostActiveDay: 'N/A', // Will be calculated after game history loads
          averageGamesPerDay: 0, // Will be calculated after game history loads
          elo_fast: (userStats as any).elo_fast,
          elo_standard: (userStats as any).elo_standard,
          rated_games_fast: (userStats as any).rated_games_fast,
          rated_games_standard: (userStats as any).rated_games_standard
        };
        
        setStats(transformedStats);
      } else {
        // No stats available - set defaults
        setStats({
          wins: 0,
          losses: 0,
          draws: 0,
          totalGames: 0,
          winRate: 0,
          winPercentage: 0,
          winStreak: 0,
          wegosWon: 0,
          wegosLost: 0,
          whiteWinRate: 0,
          blackWinRate: 0,
          gamesAsWhite: 0,
          gamesAsBlack: 0,
          longestWinStreak: 0,
          longestLossStreak: 0,
          consecutiveDays: 0,
          gamesThisWeek: 0,
          gamesThisMonth: 0,
          mostActiveDay: 'N/A',
          averageGamesPerDay: 0
        });
      }

      // Fetch game history for the specified user or current user
      const currentUser = AuthService.getCurrentUser();
      if (currentUser) {
        const result = viewingUserId
          ? await ApiService.getGameHistoryById(viewingUserId, 1000)
          : await ApiService.getGameHistory(1000); // Get more games to calculate all stats accurately
        if (result.games) {
          setGameHistory(result.games);
          
          // Calculate Wegos Won and Lost from game history
          const wegosWon = result.games.filter(g => g.result === 'win' && g.win_type === 'wego').length;
          const wegosLost = result.games.filter(g => g.result === 'loss' && g.win_type === 'wego').length;
          
          // Calculate Color Performance
          const whiteGames = result.games.filter(g => g.player_color === 'white');
          const blackGames = result.games.filter(g => g.player_color === 'black');
          const gamesAsWhite = whiteGames.length;
          const gamesAsBlack = blackGames.length;
          const whiteWins = whiteGames.filter(g => g.result === 'win').length;
          const blackWins = blackGames.filter(g => g.result === 'win').length;
          const whiteWinRate = gamesAsWhite > 0 ? (whiteWins / gamesAsWhite) * 100 : 0;
          const blackWinRate = gamesAsBlack > 0 ? (blackWins / gamesAsBlack) * 100 : 0;
          
          // Calculate Streaks
          let longestWinStreak = 0;
          let longestLossStreak = 0;
          let currentWinStreak = 0;
          let currentLossStreak = 0;
          
          // Sort games by timestamp (oldest first) to calculate streaks chronologically
          const sortedGames = [...result.games].sort((a, b) => {
            const dateA = new Date(a.timestamp.includes('T') || a.timestamp.includes('Z') || a.timestamp.includes('+') 
              ? a.timestamp 
              : a.timestamp + ' UTC');
            const dateB = new Date(b.timestamp.includes('T') || b.timestamp.includes('Z') || b.timestamp.includes('+') 
              ? b.timestamp 
              : b.timestamp + ' UTC');
            return dateA.getTime() - dateB.getTime();
          });
          
          sortedGames.forEach(game => {
            if (game.result === 'win') {
              currentWinStreak++;
              currentLossStreak = 0;
              longestWinStreak = Math.max(longestWinStreak, currentWinStreak);
            } else if (game.result === 'loss') {
              currentLossStreak++;
              currentWinStreak = 0;
              longestLossStreak = Math.max(longestLossStreak, currentLossStreak);
            } else {
              // Draw resets both streaks
              currentWinStreak = 0;
              currentLossStreak = 0;
            }
          });
          
          // Calculate Current Consecutive Days Streak (active streak counting backwards from today)
          let consecutiveDays = 0;
          if (result.games.length > 0) {
            // Get unique dates from game history (normalize to date only, no time)
            const uniqueDates = new Set<string>();
            result.games.forEach(g => {
              const gameDate = new Date(g.timestamp.includes('T') || g.timestamp.includes('Z') || g.timestamp.includes('+') 
                ? g.timestamp 
                : g.timestamp + ' UTC');
              // Normalize to date string (YYYY-MM-DD) in local timezone
              const dateStr = gameDate.toLocaleDateString('en-CA'); // Returns YYYY-MM-DD format
              uniqueDates.add(dateStr);
            });
            
            // Get today's date in local timezone (YYYY-MM-DD format)
            const today = new Date();
            const todayStr = today.toLocaleDateString('en-CA');
            
            // Get yesterday's date
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            const yesterdayStr = yesterday.toLocaleDateString('en-CA');
            
            // Check if there was activity today or yesterday (allowing for timezone differences)
            // If no activity today or yesterday, streak is 0 (broken)
            if (!uniqueDates.has(todayStr) && !uniqueDates.has(yesterdayStr)) {
              consecutiveDays = 0;
            } else {
              // Start counting backwards from today (or yesterday if no activity today)
              let checkDate = uniqueDates.has(todayStr) ? todayStr : yesterdayStr;
              let streak = 0;
              let currentDate = new Date(checkDate + 'T00:00:00');
              
              // Count backwards day by day
              while (true) {
                const dateStr = currentDate.toLocaleDateString('en-CA');
                
                if (uniqueDates.has(dateStr)) {
                  streak++;
                  // Move back one day
                  currentDate.setDate(currentDate.getDate() - 1);
                } else {
                  // Gap found - streak ends here
                  break;
                }
              }
              
              consecutiveDays = streak;
            }
          }
          
          // Calculate Time-based Stats
          const now = new Date();
          const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          
          const gamesThisWeek = result.games.filter(g => {
            const gameDate = new Date(g.timestamp.includes('T') || g.timestamp.includes('Z') || g.timestamp.includes('+') 
              ? g.timestamp 
              : g.timestamp + ' UTC');
            return gameDate >= oneWeekAgo;
          }).length;
          
          const gamesThisMonth = result.games.filter(g => {
            const gameDate = new Date(g.timestamp.includes('T') || g.timestamp.includes('Z') || g.timestamp.includes('+') 
              ? g.timestamp 
              : g.timestamp + ' UTC');
            return gameDate >= oneMonthAgo;
          }).length;
          
          // Most Active Day
          let mostActiveDay = 'N/A';
          if (result.games.length > 0) {
            const dayCounts: { [key: string]: number } = {
              'Sunday': 0,
              'Monday': 0,
              'Tuesday': 0,
              'Wednesday': 0,
              'Thursday': 0,
              'Friday': 0,
              'Saturday': 0
            };
            
            result.games.forEach(g => {
              const gameDate = new Date(g.timestamp.includes('T') || g.timestamp.includes('Z') || g.timestamp.includes('+') 
                ? g.timestamp 
                : g.timestamp + ' UTC');
              const dayName = gameDate.toLocaleDateString('en-US', { weekday: 'long' });
              dayCounts[dayName]++;
            });
            
            const maxCount = Math.max(...Object.values(dayCounts));
            if (maxCount > 0) {
              mostActiveDay = Object.entries(dayCounts).find(([_, count]) => count === maxCount)?.[0] || 'N/A';
            }
          }
          
          // Average Games Per Day
          let averageGamesPerDay = 0;
          if (result.games.length > 0) {
            const firstGame = sortedGames[0];
            const lastGame = sortedGames[sortedGames.length - 1];
            const firstDate = new Date(firstGame.timestamp.includes('T') || firstGame.timestamp.includes('Z') || firstGame.timestamp.includes('+') 
              ? firstGame.timestamp 
              : firstGame.timestamp + ' UTC');
            const lastDate = new Date(lastGame.timestamp.includes('T') || lastGame.timestamp.includes('Z') || lastGame.timestamp.includes('+') 
              ? lastGame.timestamp 
              : lastGame.timestamp + ' UTC');
            const daysDiff = Math.max(1, Math.ceil((lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24)));
            averageGamesPerDay = result.games.length / daysDiff;
          }
          
          // Update stats with all calculated values
          setStats(prev => prev ? {
            ...prev,
            wegosWon,
            wegosLost,
            whiteWinRate,
            blackWinRate,
            gamesAsWhite,
            gamesAsBlack,
            longestWinStreak,
            longestLossStreak,
            consecutiveDays,
            gamesThisWeek,
            gamesThisMonth,
            mostActiveDay,
            averageGamesPerDay
          } : prev);
        }
      }
    } catch (err: any) {
      console.error('Error fetching statistics:', err);
      setError(err.message || 'Failed to load statistics');
      // Set default stats on error to prevent crash
      setStats({
        wins: 0,
        losses: 0,
        draws: 0,
        totalGames: 0,
        winRate: 0,
        winPercentage: 0,
        winStreak: 0,
        wegosWon: 0,
        wegosLost: 0,
        whiteWinRate: 0,
        blackWinRate: 0,
        gamesAsWhite: 0,
        gamesAsBlack: 0,
        longestWinStreak: 0,
        longestLossStreak: 0,
        consecutiveDays: 0,
        gamesThisWeek: 0,
        gamesThisMonth: 0,
        mostActiveDay: 'N/A',
        averageGamesPerDay: 0
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper function to parse game timestamp to Date object
  const parseGameDate = (timestamp: string): Date => {
    if (timestamp.includes('T') || timestamp.includes('Z') || timestamp.includes('+')) {
      return new Date(timestamp);
    } else {
      return new Date(timestamp + ' UTC');
    }
  };

  // Helper function to get date string in YYYY-MM-DD format (for date input)
  const getDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Filter games by date range and optional tournament mode
  const getFilteredGames = (): GameRecord[] => {
    return gameHistory.filter(game => {
      if (tournamentOnly && game.game_mode !== 'tournament') {
        return false;
      }

      if (!fromDate && !toDate) {
        return true;
      }

      const gameDate = parseGameDate(game.timestamp);
      // Get date components in local timezone
      const gameYear = gameDate.getFullYear();
      const gameMonth = gameDate.getMonth();
      const gameDay = gameDate.getDate();
      const gameDateOnly = new Date(gameYear, gameMonth, gameDay);

      if (fromDate && toDate) {
        // Parse date strings as local dates (YYYY-MM-DD format)
        const [fromYear, fromMonth, fromDay] = fromDate.split('-').map(Number);
        const [toYear, toMonth, toDay] = toDate.split('-').map(Number);
        const from = new Date(fromYear, fromMonth - 1, fromDay);
        const to = new Date(toYear, toMonth - 1, toDay);
        to.setHours(23, 59, 59, 999); // Include entire end date
        return gameDateOnly >= from && gameDateOnly <= to;
      } else if (fromDate) {
        const [fromYear, fromMonth, fromDay] = fromDate.split('-').map(Number);
        const from = new Date(fromYear, fromMonth - 1, fromDay);
        return gameDateOnly >= from;
      } else if (toDate) {
        const [toYear, toMonth, toDay] = toDate.split('-').map(Number);
        const to = new Date(toYear, toMonth - 1, toDay);
        to.setHours(23, 59, 59, 999); // Include entire end date
        return gameDateOnly <= to;
      }
      return true;
    });
  };

  // Quick filter handlers
  const handleQuickFilter = (filter: 'today' | 'thisWeek' | 'thisMonth') => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (filter === 'today') {
      setFromDate(getDateString(today));
      setToDate(getDateString(today));
    } else if (filter === 'thisWeek') {
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
      setFromDate(getDateString(weekStart));
      setToDate(getDateString(today));
    } else if (filter === 'thisMonth') {
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      setFromDate(getDateString(monthStart));
      setToDate(getDateString(today));
    }
  };

  const clearFilters = () => {
    setFromDate('');
    setToDate('');
    setTournamentOnly(false);
  };

  const filteredGames = getFilteredGames();

  const getWinRateColor = (percentage: number) => {
    if (percentage >= 80) return '#28a745'; // Green for high win rate
    if (percentage >= 60) return '#ffc107'; // Yellow for medium win rate
    if (percentage >= 40) return '#fd7e14'; // Orange for low win rate
    return '#dc3545'; // Red for very low win rate
  };

  const getWinRateEmoji = (percentage: number) => {
    if (percentage >= 80) return '🏆';
    if (percentage >= 60) return '🥇';
    if (percentage >= 40) return '🥈';
    return '🥉';
  };

  return (
    <div 
      className="statistics-page"
      style={isModal ? { 
        display: 'flex', 
        flexDirection: 'column',
        padding: 0, 
        maxWidth: 860, 
        width: '100%', 
        margin: '0 auto',
        height: '90vh',
        maxHeight: '90vh',
        overflow: 'hidden'
      } : undefined}
    >
      <div className="statistics-container" style={isModal ? { 
        marginLeft: 0, 
        padding: '12px 16px',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden'
      } : undefined}>
        {/* Header */}
        <div className="statistics-header" style={isModal ? {
          padding: '10px 16px',
          marginBottom: '8px',
          flex: '0 0 auto'
        } : { position: 'relative' }}>
          {!isModal && (
            <PageCloseButton onClick={onBack} />
          )}
          <h1 style={isModal ? { fontSize: '1.1rem', margin: 0 } : undefined}>
            <img 
              src="https://img.icons8.com/?size=100&id=iMOL8BuPplly&format=png&color=FFFFFF" 
              alt="Trophy" 
              style={isModal ? { width: '24px', height: '24px', verticalAlign: 'middle', marginRight: '8px', display: 'inline-block' } : { width: '40px', height: '40px', verticalAlign: 'middle', marginRight: '12px', display: 'inline-block' }}
            />
            {viewingUsername || user.username}
            <img 
              src="https://img.icons8.com/?size=100&id=iMOL8BuPplly&format=png&color=FFFFFF" 
              alt="Trophy" 
              style={isModal ? { width: '24px', height: '24px', verticalAlign: 'middle', marginLeft: '8px', display: 'inline-block' } : { width: '40px', height: '40px', verticalAlign: 'middle', marginLeft: '12px', display: 'inline-block' }}
            />
          </h1>
          {stats && (
            <div style={{ textAlign: 'center', marginTop: '6px', fontSize: isModal ? '13px' : '15px', color: '#495057' }}>
              <span title={eloNumberTitle('standard', stats.rated_games_standard)}>
                Standard {formatEloNumber(stats.elo_standard, stats.rated_games_standard)}
              </span>
              {' · '}
              <span title={eloNumberTitle('fast', stats.rated_games_fast)}>
                Fast {formatEloNumber(stats.elo_fast, stats.rated_games_fast)}
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="statistics-content" style={isModal ? {
          padding: '8px 12px',
          flex: '1 1 auto',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        } : undefined}>
          {loading ? (
            <div className="loading-section">
              <div className="loading-spinner"></div>
              <p>Analyzing statistics...</p>
            </div>
          ) : error ? (
            <div className="error-section">
              <p>❌ {error}</p>
              <button onClick={fetchStatistics} className="retry-btn">
                🔄 Retry
              </button>
            </div>
          ) : stats && typeof stats.winPercentage === 'number' ? (
            <>
              {/* Main Stats Grid */}
              <div className="stats-grid" style={isModal ? {
                gap: '8px',
                marginBottom: '8px',
                flex: '0 0 auto'
              } : undefined}>
                <div className="stat-card total-games" style={isModal ? { padding: '8px 10px' } : undefined}>
                  <div className="stat-icon" style={isModal ? { fontSize: '18px', marginBottom: '4px' } : undefined}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" id="Add-Circle-Bold--Streamline-Ultimate" height={isModal ? "18" : "14"} width={isModal ? "18" : "14"}>
                      <desc>
                        Add Circle Bold Streamline Icon: https://streamlinehq.com
                      </desc>
                      <path fill="#c9f7ca" d="M11.9999 23.2044c6.1881 0 11.2045 -5.0164 11.2045 -11.2044S18.188 0.7956 11.9999 0.7956C5.812 0.7956 0.7956 5.812 0.7956 12S5.812 23.2044 11.9999 23.2044Z" stroke-width="1"></path>
                      <path fill="#78eb7b" d="M11.9999 20.1486c2.7074 0.0007 5.3236 -0.979 7.3645 -2.7578 2.041 -1.7789 3.369 -4.2366 3.7381 -6.9187 0.2064 1.5202 0.0985 3.0665 -0.3173 4.5433 -0.4157 1.4768 -1.1303 2.8524 -2.0994 4.0417 -0.9692 1.1892 -2.1722 2.167 -3.5345 2.8722 -1.3623 0.7053 -2.8551 1.1233 -4.3856 1.2282 -1.5307 0.1048 -3.0664 -0.1057 -4.5123 -0.6188 -1.4458 -0.5129 -2.7709 -1.3174 -3.8931 -2.3635 -1.1222 -1.046 -2.0177 -2.3113 -2.6308 -3.7176C1.1163 15.0514 0.7984 13.5342 0.7956 12c-0.0007 -0.5111 0.0334 -1.0214 0.1019 -1.5279 0.3692 2.6822 1.697 5.1399 3.738 6.9187 2.041 1.7788 4.6571 2.7585 7.3644 2.7578Z" stroke-width="1"></path>
                      <path stroke="#191919" strokeLinecap="round" strokeLinejoin="round" d="M11.9999 23.2044c6.1881 0 11.2045 -5.0164 11.2045 -11.2044S18.188 0.7956 11.9999 0.7956C5.812 0.7956 0.7956 5.812 0.7956 12S5.812 23.2044 11.9999 23.2044Z" stroke-width="1"></path>
                      <path fill="#ffffff" stroke="#191919" strokeLinecap="round" strokeLinejoin="round" d="M19.1304 10.9813c0 -0.2701 -0.1074 -0.5292 -0.2984 -0.7202 -0.1909 -0.1909 -0.45 -0.2984 -0.7201 -0.2984h-4.0744V5.8884c0 -0.2701 -0.1073 -0.5292 -0.2983 -0.7202 -0.191 -0.191 -0.4502 -0.2983 -0.7203 -0.2983h-2.0371c-0.2701 0 -0.5293 0.1073 -0.7203 0.2983s-0.2983 0.4501 -0.2983 0.7202v4.0743H5.8889c-0.2702 0 -0.5293 0.1075 -0.7203 0.2984 -0.191 0.191 -0.2983 0.4501 -0.2983 0.7202v2.0372c0 0.2701 0.1073 0.5292 0.2983 0.7202 0.191 0.191 0.4501 0.2984 0.7203 0.2984h4.0743v4.0743c0 0.2702 0.1073 0.5293 0.2983 0.7203 0.191 0.1909 0.4501 0.2982 0.7203 0.2982h2.0371c0.2701 0 0.5292 -0.1073 0.7203 -0.2982 0.191 -0.191 0.2983 -0.4501 0.2983 -0.7203v-4.0743h4.0744c0.2701 0 0.5292 -0.1074 0.7201 -0.2984 0.191 -0.191 0.2984 -0.4501 0.2984 -0.7202v-2.0372Z" stroke-width="1"></path>
                    </svg>
                  </div>
                  <div className="stat-value" style={isModal ? { fontSize: '16px', marginBottom: '2px' } : undefined}>{stats?.totalGames || 0}</div>
                  <div className="stat-label" style={isModal ? { fontSize: '10px' } : { fontSize: '9px' }}>Games</div>
                </div>

                <div className="stat-card wins" style={isModal ? { padding: '8px 10px' } : undefined}>
                  <div className="stat-icon" style={isModal ? { fontSize: '18px', marginBottom: '4px' } : undefined}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" id="Ranking-People-First--Streamline-Ultimate" height="14" width="14">
                      <desc>
                        Ranking People First Streamline Icon: https://streamlinehq.com
                      </desc>
                      <path fill="#ffbc44" stroke="#191919" strokeLinecap="round" strokeLinejoin="round" d="M22.5217 17.0317h-7.1739v-4.7826c0 -0.1268 -0.0504 -0.2485 -0.1401 -0.3381 -0.0896 -0.0897 -0.2113 -0.1401 -0.3381 -0.1401H9.13043c-0.12684 0 -0.24849 0.0504 -0.33818 0.1401 -0.08969 0.0896 -0.14008 0.2113 -0.14008 0.3381v4.7826H1.47826c-0.12684 0 -0.24849 0.0504 -0.33818 0.1401C1.05039 17.2615 1 17.3832 1 17.51v4.7826c0 0.1269 0.05039 0.2485 0.14008 0.3382 0.08969 0.0897 0.21134 0.1401 0.33818 0.1401H22.5217c0.1269 0 0.2485 -0.0504 0.3382 -0.1401S23 22.4195 23 22.2926V17.51c0 -0.1268 -0.0504 -0.2485 -0.1401 -0.3382s-0.2113 -0.1401 -0.3382 -0.1401Z" strokeWidth="1"></path>
                      <path fill="#e3e3e3" d="M7.69629 9.37965c0.01817 -0.52608 0.14443 -1.04261 0.37304 -1.518 0.264 -0.528 1.36592 -0.89339 2.74997 -1.40608 0.374 -0.1387 0.3138 -1.012 0.1473 -1.19374 -0.2635 -0.28618 -0.4637 -0.62481 -0.5873 -0.99372 -0.1237 -0.3689 -0.168 -0.75976 -0.1301 -1.14698 -0.0187 -0.24134 0.0127 -0.48395 0.0922 -0.7126 0.0795 -0.22864 0.2054 -0.43838 0.3698 -0.61604 0.1644 -0.17767 0.3638 -0.31942 0.5856 -0.41637 0.2218 -0.09695 0.4613 -0.14699 0.7034 -0.14699 0.242 0 0.4815 0.05004 0.7033 0.14699 0.2218 0.09695 0.4212 0.2387 0.5856 0.41637 0.1644 0.17766 0.2903 0.3874 0.3698 0.61604 0.0795 0.22865 0.1109 0.47126 0.0922 0.7126 0.0378 0.38721 -0.0066 0.77804 -0.1302 1.14693 -0.1236 0.36888 -0.3237 0.70752 -0.5872 0.99377 -0.1655 0.18269 -0.2267 1.05217 0.1473 1.19374 1.3841 0.51269 2.4869 0.87808 2.75 1.40608 0.2295 0.47444 0.3558 0.99192 0.374 1.518" strokeWidth="1"></path>
                      <path stroke="#191919" strokeLinecap="round" strokeLinejoin="round" d="M7.69629 9.37965c0.01817 -0.52608 0.14443 -1.04261 0.37304 -1.518 0.264 -0.528 1.36592 -0.89339 2.74997 -1.40608 0.374 -0.1387 0.3138 -1.012 0.1473 -1.19374 -0.2635 -0.28618 -0.4637 -0.62481 -0.5873 -0.99372 -0.1237 -0.3689 -0.168 -0.75976 -0.1301 -1.14698 -0.0187 -0.24134 0.0127 -0.48395 0.0922 -0.7126 0.0795 -0.22864 0.2054 -0.43838 0.3698 -0.61604 0.1644 -0.17767 0.3638 -0.31942 0.5856 -0.41637 0.2218 -0.09695 0.4613 -0.14699 0.7034 -0.14699 0.242 0 0.4815 0.05004 0.7033 0.14699 0.2218 0.09695 0.4212 0.2387 0.5856 0.41637 0.1644 0.17766 0.2903 0.3874 0.3698 0.61604 0.0795 0.22865 0.1109 0.47126 0.0922 0.7126 0.0378 0.38721 -0.0066 0.77804 -0.1302 1.14693 -0.1236 0.36888 -0.3237 0.70752 -0.5872 0.99377 -0.1655 0.18269 -0.2267 1.05217 0.1473 1.19374 1.3841 0.51269 2.4869 0.87808 2.75 1.40608 0.2295 0.47444 0.3558 0.99192 0.374 1.518" strokeWidth="1"></path>
                      <path fill="#e3e3e3" d="M5.98539 14.1622c-0.4113 -0.1684 -0.89435 -0.3405 -1.41469 -0.5338 -0.374 -0.1387 -0.31374 -1.012 -0.14731 -1.1937 0.26359 -0.2862 0.46375 -0.6248 0.58737 -0.9937 0.12363 -0.3689 0.16794 -0.7598 0.13002 -1.147 0.02268 -0.2411 -0.00604 -0.48422 -0.08426 -0.71338 -0.07821 -0.22916 -0.20415 -0.43913 -0.36948 -0.61604 -0.16534 -0.1769 -0.36632 -0.31673 -0.58967 -0.41024 -0.22336 -0.09352 -0.46401 -0.13859 -0.70607 -0.13225 -0.24221 -0.00662 -0.48308 0.03825 -0.70666 0.13164 -0.22359 0.0934 -0.4248 0.23318 -0.59035 0.41012 -0.16555 0.17694 -0.29165 0.387 -0.36998 0.6163 -0.07833 0.2293 -0.10709 0.47265 -0.0844 0.71385 -0.03791 0.3872 0.00639 0.7781 0.13002 1.147 0.12363 0.3689 0.32379 0.7075 0.58737 0.9937 0.16548 0.1827 0.2267 1.0522 -0.1473 1.1937 -0.43809 0.1617 -0.84652 0.3099 -1.21 0.4525" strokeWidth="1"></path>
                      <path stroke="#191919" strokeLinecap="round" strokeLinejoin="round" d="M5.98539 14.1622c-0.4113 -0.1684 -0.89435 -0.3405 -1.41469 -0.5338 -0.374 -0.1387 -0.31374 -1.012 -0.14731 -1.1937 0.26359 -0.2862 0.46375 -0.6248 0.58737 -0.9937 0.12363 -0.3689 0.16794 -0.7598 0.13002 -1.147 0.02268 -0.2411 -0.00604 -0.48422 -0.08426 -0.71338 -0.07821 -0.22916 -0.20415 -0.43913 -0.36948 -0.61604 -0.16534 -0.1769 -0.36632 -0.31673 -0.58967 -0.41024 -0.22336 -0.09352 -0.46401 -0.13859 -0.70607 -0.13225 -0.24221 -0.00662 -0.48308 0.03825 -0.70666 0.13164 -0.22359 0.0934 -0.4248 0.23318 -0.59035 0.41012 -0.16555 0.17694 -0.29165 0.387 -0.36998 0.6163 -0.07833 0.2293 -0.10709 0.47265 -0.0844 0.71385 -0.03791 0.3872 0.00639 0.7781 0.13002 1.147 0.12363 0.3689 0.32379 0.7075 0.58737 0.9937 0.16548 0.1827 0.2267 1.0522 -0.1473 1.1937 -0.43809 0.1617 -0.84652 0.3099 -1.21 0.4525" strokeWidth="1"></path>
                      <path fill="#e3e3e3" d="M18.0127 14.1623c0.4113 -0.1684 0.8943 -0.3405 1.4147 -0.5338 0.374 -0.1387 0.3137 -1.012 0.1473 -1.1937 -0.2636 -0.2862 -0.4638 -0.6248 -0.5874 -0.9937 -0.1236 -0.3689 -0.1679 -0.7598 -0.13 -1.147 -0.0187 -0.2413 0.0127 -0.48395 0.0922 -0.71259 0.0795 -0.22864 0.2054 -0.43838 0.3698 -0.61605 0.1644 -0.17766 0.3638 -0.31942 0.5856 -0.41637 0.2218 -0.09695 0.4612 -0.14699 0.7033 -0.14699 0.2421 0 0.4815 0.05004 0.7034 0.14699 0.2218 0.09695 0.4211 0.23871 0.5855 0.41637 0.1645 0.17767 0.2904 0.38741 0.3699 0.61605 0.0795 0.22864 0.1109 0.47129 0.0921 0.71259 0.0378 0.3872 -0.0065 0.778 -0.1302 1.1469 -0.1236 0.3689 -0.3237 0.7076 -0.5872 0.9938 -0.1654 0.1827 -0.2267 1.0522 0.1473 1.1937 0.4381 0.1617 0.8466 0.31 1.211 0.4525" strokeWidth="1"></path>
                      <path stroke="#191919" strokeLinecap="round" strokeLinejoin="round" d="M18.0127 14.1623c0.4113 -0.1684 0.8943 -0.3405 1.4147 -0.5338 0.374 -0.1387 0.3137 -1.012 0.1473 -1.1937 -0.2636 -0.2862 -0.4638 -0.6248 -0.5874 -0.9937 -0.1236 -0.3689 -0.1679 -0.7598 -0.13 -1.147 -0.0187 -0.2413 0.0127 -0.48395 0.0922 -0.71259 0.0795 -0.22864 0.2054 -0.43838 0.3698 -0.61605 0.1644 -0.17766 0.3638 -0.31942 0.5856 -0.41637 0.2218 -0.09695 0.4612 -0.14699 0.7033 -0.14699 0.2421 0 0.4815 0.05004 0.7034 0.14699 0.2218 0.09695 0.4211 0.23871 0.5855 0.41637 0.1645 0.17767 0.2904 0.38741 0.3699 0.61605 0.0795 0.22864 0.1109 0.47129 0.0921 0.71259 0.0378 0.3872 -0.0065 0.778 -0.1302 1.1469 -0.1236 0.3689 -0.3237 0.7076 -0.5872 0.9938 -0.1654 0.1827 -0.2267 1.0522 0.1473 1.1937 0.4381 0.1617 0.8466 0.31 1.211 0.4525" strokeWidth="1"></path>
                    </svg>
                  </div>
                  <div className="stat-value" style={isModal ? { fontSize: '16px', marginBottom: '2px' } : undefined}>{stats?.wins || 0}</div>
                  <div className="stat-label" style={isModal ? { fontSize: '10px' } : { fontSize: '9px' }}>Wins</div>
                </div>

                <div className="stat-card losses" style={isModal ? { padding: '8px 10px' } : undefined}>
                  <div className="stat-icon" style={isModal ? { fontSize: '18px', marginBottom: '4px' } : undefined}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" id="Graph-Stats-Descend--Streamline-Ultimate" height="14" width="14">
                      <desc>
                        Graph Stats Descend Streamline Icon: https://streamlinehq.com
                      </desc>
                      <path fill="#ffbfc5" d="M11.9998 22.9319c6.0376 0 10.9319 -4.8943 10.9319 -10.9319 0 -6.0375 -4.8943 -10.9318 -10.9319 -10.9318C5.9623 1.0682 1.068 5.9625 1.068 12c0 6.0376 4.8943 10.9319 10.9318 10.9319Z" stroke-width="1"></path>
                      <path stroke="#191919" strokeLinecap="round" strokeLinejoin="round" d="m0.8079 5.4929 7.6377 8.5893c0.1868 0.2063 0.4457 0.3328 0.7232 0.3536 0.2775 0.0208 0.5523 -0.066 0.7677 -0.2422l4.6476 -3.873c0.2152 -0.1761 0.4901 -0.2625 0.7674 -0.2412 0.2773 0.0214 0.5356 0.1488 0.7215 0.3557l7.1191 8.0688" stroke-width="1"></path>
                      <path stroke="#191919" strokeLinecap="round" strokeLinejoin="round" d="M15.9683 18.5071h7.2235v-6.225" stroke-width="1"></path>
                    </svg>
                  </div>
                  <div className="stat-value" style={isModal ? { fontSize: '16px', marginBottom: '2px' } : undefined}>{stats?.losses || 0}</div>
                  <div className="stat-label" style={isModal ? { fontSize: '10px' } : { fontSize: '9px' }}>Losses</div>
                </div>

                <div className="stat-card draws" style={isModal ? { padding: '8px 10px' } : undefined}>
                  <div className="stat-icon" style={isModal ? { fontSize: '18px', marginBottom: '4px' } : undefined}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" id="Cursor-Hand-2--Streamline-Ultimate" height="14" width="14">
                      <desc>
                        Cursor Hand 2 Streamline Icon: https://streamlinehq.com
                      </desc>
                      <path fill="#ffdda1" d="M17.533 12.2602v-4.685c0 -0.4142 0.1644 -0.8114 0.4573 -1.1043 0.2928 -0.2929 0.6901 -0.4574 1.1044 -0.4574 0.4141 0 0.8114 0.1645 1.1042 0.4574 0.2929 0.2929 0.4574 0.6901 0.4574 1.1043v10.4112c0 1.3807 -0.5484 2.7047 -1.5247 3.6809 -0.9762 0.9763 -2.3003 1.5247 -3.6809 1.5247h-3.4722c-0.8769 -0.0005 -1.7396 -0.222 -2.5081 -0.6444 -0.7686 -0.4224 -1.4181 -1.0319 -1.8885 -1.772 -1.7929 -2.8235 -4.0157 -7.4743 -4.0157 -7.4743 -0.1726 -0.2489 -0.2488 -0.5522 -0.2145 -0.8533 0.0344 -0.3011 0.1769 -0.5793 0.4012 -0.7831 0.2243 -0.2037 0.5149 -0.319 0.8179 -0.3245 0.3029 -0.0053 0.5975 0.0995 0.8289 0.2951l2.7652 3.2275V4.4518c0 -0.4142 0.1645 -0.8114 0.4574 -1.1043 0.2929 -0.2928 0.6901 -0.4574 1.1043 -0.4574s0.8114 0.1646 1.1043 0.4574c0.2928 0.2929 0.4574 0.6901 0.4574 1.1043V2.3696c0 -0.4142 0.1645 -0.8114 0.4574 -1.1043S12.4358 0.8079 12.85 0.8079s0.8114 0.1645 1.1043 0.4574c0.2928 0.2929 0.4574 0.6901 0.4574 1.1043v2.0822c0 -0.4142 0.1644 -0.8114 0.4574 -1.1043 0.2929 -0.2928 0.69 -0.4574 1.1043 -0.4574s0.8114 0.1646 1.1043 0.4574c0.2928 0.2929 0.4574 0.6901 0.4574 1.1043v7.8084h-0.0021Z" stroke-width="1"></path>
                      <path fill="#ffdda1" d="M11.2863 10.4008V4.4519c0 -0.4142 -0.1645 -0.8114 -0.4575 -1.1043 -0.2928 -0.2929 -0.69 -0.4574 -1.1042 -0.4574 -0.4142 0 -0.8114 0.1645 -1.1043 0.4574 -0.2928 0.2929 -0.4574 0.6901 -0.4574 1.1043v6.8819c1 -0.4352 2.0487 -0.7484 3.1234 -0.933Z" stroke-width="1"></path>
                      <path fill="#ffdda1" d="M13.8891 10.178c2.3618 -0.0272 4.6913 0.5499 6.7673 1.6762v-4.279c0.0007 -0.2051 -0.039 -0.4083 -0.1168 -0.598 -0.0779 -0.1898 -0.1924 -0.3623 -0.3369 -0.5078 -0.1446 -0.1455 -0.3163 -0.2611 -0.5055 -0.3402 -0.1892 -0.0791 -0.3922 -0.1202 -0.5972 -0.1209 -0.2051 -0.0007 -0.4083 0.039 -0.5981 0.1169 -0.1897 0.0778 -0.3623 0.1923 -0.5078 0.3368 -0.1455 0.1446 -0.2611 0.3163 -0.3402 0.5055 -0.0791 0.1893 -0.1201 0.3922 -0.1208 0.5973v-3.113c0 -0.4142 -0.1646 -0.8114 -0.4575 -1.1043 -0.2928 -0.2928 -0.6901 -0.4574 -1.1042 -0.4574 -0.4142 0 -0.8114 0.1646 -1.1043 0.4574 -0.2929 0.2929 -0.4574 0.6901 -0.4574 1.1043V2.3696c0 -0.4142 -0.1646 -0.8114 -0.4575 -1.1043 -0.2928 -0.2929 -0.6901 -0.4574 -1.1042 -0.4574 -0.4142 0 -0.8114 0.1645 -1.1043 0.4574s-0.4574 0.6901 -0.4574 1.1043v8.0312c0.8596 -0.1485 1.7304 -0.2232 2.6027 -0.2228Z" stroke-width="1"></path>
                      <path stroke="#191919" stroke-linecap="round" stroke-linejoin="round" d="M11.2874 4.4518V2.3696c0 -0.4142 0.1645 -0.8114 0.4574 -1.1043 0.2928 -0.2929 0.6901 -0.4574 1.1042 -0.4574 0.4143 0 0.8116 0.1645 1.1044 0.4574 0.2929 0.2929 0.4573 0.6901 0.4573 1.1043v2.0822" stroke-width="1"></path>
                      <path stroke="#191919" stroke-linecap="round" stroke-linejoin="round" d="M14.4107 10.6986V4.4519c0 -0.4142 0.1646 -0.8114 0.4575 -1.1043 0.2928 -0.2929 0.6901 -0.4574 1.1042 -0.4574 0.4142 0 0.8115 0.1645 1.1043 0.4574 0.2929 0.2929 0.4574 0.6901 0.4574 1.1043v3.1233" stroke-width="1"></path>
                      <path stroke="#191919" stroke-linecap="round" stroke-linejoin="round" d="M17.533 12.2603V7.5752c0 -0.4141 0.1644 -0.8114 0.4573 -1.1042 0.2928 -0.2929 0.6901 -0.4574 1.1044 -0.4574 0.4141 0 0.8114 0.1645 1.1042 0.4574 0.2929 0.2928 0.4574 0.69 0.4574 1.1042v10.4113c0 1.3806 -0.5484 2.7046 -1.5247 3.681 -0.9762 0.9762 -2.3003 1.5246 -3.6809 1.5246h-3.4722c-0.8769 -0.0004 -1.7396 -0.2221 -2.5081 -0.6445 -0.7686 -0.4224 -1.4181 -1.0319 -1.8885 -1.772 -1.7929 -2.8235 -4.0157 -7.4741 -4.0157 -7.4741 -0.1726 -0.2491 -0.2488 -0.5524 -0.2145 -0.8534 0.0344 -0.301 0.1769 -0.5794 0.4012 -0.7832 0.2243 -0.2036 0.5149 -0.319 0.8179 -0.3243 0.3029 -0.0055 0.5975 0.0993 0.8289 0.295l2.7652 3.2276V4.4519c0 -0.4142 0.1645 -0.8114 0.4574 -1.1043 0.2929 -0.2929 0.6901 -0.4574 1.1043 -0.4574s0.8114 0.1645 1.1043 0.4574c0.2928 0.2929 0.4574 0.6901 0.4574 1.1043v6.2467" stroke-width="1"></path>
                    </svg>
                  </div>
                  <div className="stat-value" style={isModal ? { fontSize: '16px', marginBottom: '2px' } : undefined}>{stats?.draws || 0}</div>
                  <div className="stat-label" style={isModal ? { fontSize: '10px' } : { fontSize: '9px' }}>Draws</div>
                </div>

                <div className="stat-card wegos-won" style={isModal ? { padding: '8px 10px' } : undefined}>
                  <div className="stat-icon" style={isModal ? { fontSize: '18px', marginBottom: '4px' } : { fontSize: '14px', marginBottom: '4px' }}>🏆</div>
                  <div className="stat-value" style={isModal ? { fontSize: '16px', marginBottom: '2px' } : undefined}>{stats?.wegosWon || 0}</div>
                  <div className="stat-label" style={isModal ? { fontSize: '10px' } : { fontSize: '9px' }}>
                    <div>Wegos</div>
                    <div>Won</div>
                  </div>
                </div>

                <div className="stat-card wegos-lost" style={isModal ? { padding: '8px 10px' } : undefined}>
                  <div className="stat-icon" style={isModal ? { fontSize: '18px', marginBottom: '4px' } : { fontSize: '14px', marginBottom: '4px' }}>💔</div>
                  <div className="stat-value" style={isModal ? { fontSize: '16px', marginBottom: '2px' } : undefined}>{stats?.wegosLost || 0}</div>
                  <div className="stat-label" style={isModal ? { fontSize: '10px' } : { fontSize: '9px' }}>
                    <div>Wegos</div>
                    <div>Lost</div>
                  </div>
                </div>
              </div>

              {/* Win Rate Section */}
              {stats && typeof stats.winPercentage === 'number' && (
                <div className="win-rate-section" style={isModal ? { marginBottom: '8px', flex: '0 0 auto' } : undefined}>
                  <div className="win-rate-card" style={isModal ? { padding: '6px 9px' } : undefined}>
                    <div className="win-rate-header" style={isModal ? { marginBottom: '4px' } : undefined}>
                      <h3 style={isModal ? { fontSize: '0.95rem', margin: 0 } : undefined}>Win Percentage</h3>
                      <div 
                        className="win-rate-value"
                        style={isModal ? { 
                          fontSize: '20px', 
                          margin: 0,
                          color: getWinRateColor(stats.winPercentage) 
                        } : { color: getWinRateColor(stats.winPercentage), margin: 0 }}
                      >
                        {stats.winPercentage.toFixed(1)}%
                      </div>
                    </div>
                    <div className="win-rate-bar" style={isModal ? { height: '6px' } : undefined}>
                      <div 
                        className="win-rate-fill"
                        style={{ 
                          width: `${Math.min(stats.winPercentage, 100)}%`,
                          backgroundColor: getWinRateColor(stats.winPercentage)
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Streak Statistics Section */}
              <div className="streak-stats-section" style={isModal ? {
                marginTop: '8px',
                marginBottom: '8px',
                flex: '0 0 auto'
              } : { marginTop: '12px', marginBottom: '12px' }}>
                <div className="stats-grid" style={isModal ? {
                  gap: '8px',
                  gridTemplateColumns: 'repeat(3, 1fr)'
                } : {
                  gap: '8px',
                  gridTemplateColumns: 'repeat(3, 1fr)'
                }}>
                  <div className="stat-card" style={isModal ? { padding: '8px 10px' } : undefined}>
                    <div className="stat-label" style={isModal ? { fontSize: '10px', marginBottom: '4px' } : undefined}>Longest Win Streak</div>
                    <div className="stat-value" style={isModal ? { fontSize: '16px', marginBottom: '2px', color: '#27ae60' } : { fontSize: '20px', color: '#27ae60' }}>
                      {stats.longestWinStreak}
                    </div>
                  </div>
                  <div className="stat-card" style={isModal ? { padding: '8px 10px' } : undefined}>
                    <div className="stat-label" style={isModal ? { fontSize: '10px', marginBottom: '4px' } : undefined}>Longest Loss Streak</div>
                    <div className="stat-value" style={isModal ? { fontSize: '16px', marginBottom: '2px', color: '#e74c3c' } : { fontSize: '20px', color: '#e74c3c' }}>
                      {stats.longestLossStreak}
                    </div>
                  </div>
                  <div className="stat-card" style={isModal ? { padding: '8px 10px' } : undefined}>
                    <div className="stat-label" style={isModal ? { fontSize: '10px', marginBottom: '4px' } : undefined}>Consecutive Days</div>
                    <div className="stat-value" style={isModal ? { fontSize: '16px', marginBottom: '2px', color: '#3498db' } : { fontSize: '20px', color: '#3498db' }}>
                      {stats.consecutiveDays}
                    </div>
                  </div>
                </div>
              </div>

              {/* Performance Trend */}
              {gameHistory.length > 0 && (
                <div className="performance-trend-section" style={isModal ? {
                  marginTop: '8px',
                  marginBottom: '8px',
                  flex: '0 0 auto'
                } : { marginTop: '12px', marginBottom: '12px' }}>
                  <h3 style={isModal ? { 
                    color: '#2c3e50', 
                    marginBottom: '6px',
                    fontSize: '0.95rem',
                    fontWeight: 'bold'
                  } : { 
                    color: '#2c3e50', 
                    marginBottom: '8px',
                    fontSize: '1.1rem',
                    fontWeight: 'bold'
                  }}>
                    📈 Recent Performance (Last {Math.min(gameHistory.length, 10)} Games)
                  </h3>
                  <div className="performance-trend-icons" style={{
                    display: 'flex',
                    gap: isModal ? '2px' : '6px',
                    flexWrap: isModal ? 'nowrap' : 'wrap',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%'
                  }}>
                    {gameHistory.slice(0, 10).reverse().map((game, index) => (
                      <div
                        key={game.id || index}
                        className="performance-icon"
                        style={{
                          width: isModal ? '22px' : '32px',
                          height: isModal ? '22px' : '32px',
                          borderRadius: '50%',
                          backgroundColor: game.result === 'win' ? '#28a745' : 
                                         game.result === 'loss' ? '#dc3545' : '#ffc107',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: isModal ? '10px' : '16px',
                          fontWeight: 'bold',
                          border: '2px solid white',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          flexShrink: 0
                        }}
                        title={`${game.result === 'win' ? 'Win' : game.result === 'loss' ? 'Loss' : 'Draw'} vs ${game.opponent}`}
                      >
                        {game.result === 'win' ? 'W' : game.result === 'loss' ? 'L' : 'D'}
                      </div>
                    ))}
                  </div>
                  <div style={{
                    marginTop: isModal ? '6px' : '10px',
                    textAlign: 'center',
                    fontSize: isModal ? '0.7rem' : '0.85rem',
                    color: '#666'
                  }}>
                    {gameHistory.slice(0, 10).filter(g => g.result === 'win').length}W / {gameHistory.slice(0, 10).filter(g => g.result === 'loss').length}L / {gameHistory.slice(0, 10).filter(g => g.result === 'draw').length}D
                  </div>
                </div>
              )}

              {/* Recent Match History */}
              <div className="game-history-section" style={isModal ? {
                marginTop: '8px',
                flex: '1 1 auto',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0
              } : { marginTop: '20px' }}>
                {/* Date Filter Bar */}
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: isModal ? '4px' : '6px',
                  alignItems: 'center',
                  marginBottom: isModal ? '6px' : '12px',
                  flex: '0 0 auto'
                }}>
                  <label style={{
                    fontSize: isModal ? '0.75rem' : '0.85rem',
                    color: '#2c3e50',
                    fontWeight: '500',
                    whiteSpace: 'nowrap'
                  }}>
                    From:
                  </label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    style={{
                      fontSize: isModal ? '0.75rem' : '0.85rem',
                      padding: isModal ? '3px 6px' : '4px 8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      flex: '0 0 auto',
                      minWidth: isModal ? '100px' : '120px'
                    }}
                  />
                  <label style={{
                    fontSize: isModal ? '0.75rem' : '0.85rem',
                    color: '#2c3e50',
                    fontWeight: '500',
                    whiteSpace: 'nowrap'
                  }}>
                    To:
                  </label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    style={{
                      fontSize: isModal ? '0.75rem' : '0.85rem',
                      padding: isModal ? '3px 6px' : '4px 8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      flex: '0 0 auto',
                      minWidth: isModal ? '100px' : '120px'
                    }}
                  />
                  <button
                    onClick={() => handleQuickFilter('today')}
                    style={{
                      fontSize: isModal ? '0.7rem' : '0.75rem',
                      padding: isModal ? '3px 6px' : '4px 8px',
                      border: '1px solid #0f4ea5',
                      borderRadius: '4px',
                      background: 'transparent',
                      color: '#0f4ea5',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      flex: '0 0 auto'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#0f4ea5';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#0f4ea5';
                    }}
                  >
                    Today
                  </button>
                  <button
                    onClick={() => handleQuickFilter('thisWeek')}
                    style={{
                      fontSize: isModal ? '0.7rem' : '0.75rem',
                      padding: isModal ? '3px 6px' : '4px 8px',
                      border: '1px solid #0f4ea5',
                      borderRadius: '4px',
                      background: 'transparent',
                      color: '#0f4ea5',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      flex: '0 0 auto'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#0f4ea5';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#0f4ea5';
                    }}
                  >
                    Week
                  </button>
                  <button
                    onClick={() => handleQuickFilter('thisMonth')}
                    style={{
                      fontSize: isModal ? '0.7rem' : '0.75rem',
                      padding: isModal ? '3px 6px' : '4px 8px',
                      border: '1px solid #0f4ea5',
                      borderRadius: '4px',
                      background: 'transparent',
                      color: '#0f4ea5',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      flex: '0 0 auto'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#0f4ea5';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#0f4ea5';
                    }}
                  >
                    Month
                  </button>
                  <button
                    onClick={() => setTournamentOnly(prev => !prev)}
                    style={{
                      fontSize: isModal ? '0.7rem' : '0.75rem',
                      padding: isModal ? '3px 6px' : '4px 8px',
                      border: '1px solid #7d5a00',
                      borderRadius: '4px',
                      background: tournamentOnly ? '#7d5a00' : 'transparent',
                      color: tournamentOnly ? '#f9e79f' : '#7d5a00',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      flex: '0 0 auto',
                      fontWeight: tournamentOnly ? 700 : 500
                    }}
                    onMouseEnter={(e) => {
                      if (!tournamentOnly) {
                        e.currentTarget.style.background = '#7d5a00';
                        e.currentTarget.style.color = '#f9e79f';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!tournamentOnly) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#7d5a00';
                      }
                    }}
                  >
                    Tournament
                  </button>
                  {(fromDate || toDate || tournamentOnly) && (
                    <button
                      onClick={clearFilters}
                      style={{
                        fontSize: isModal ? '0.7rem' : '0.75rem',
                        padding: isModal ? '3px 6px' : '4px 8px',
                        border: '1px solid #dc3545',
                        borderRadius: '4px',
                        background: 'transparent',
                        color: '#dc3545',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        flex: '0 0 auto'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#dc3545';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#dc3545';
                      }}
                    >
                      Clear
                    </button>
                  )}
                  <span style={{
                    fontSize: isModal ? '0.75rem' : '0.85rem',
                    color: '#666',
                    marginLeft: 'auto',
                    whiteSpace: 'nowrap',
                    flex: '0 0 auto'
                  }}>
                    Showing {filteredGames.length} of {gameHistory.length} matches
                  </span>
                </div>
                <div className="game-history-list" style={isModal ? {
                  overflowY: 'auto',
                  flex: '1 1 auto',
                  minHeight: 0,
                  paddingRight: '4px'
                } : undefined}>
                  {gameHistory.length > 0 ? (
                    filteredGames.length > 0 ? (
                      filteredGames.map((game, index) => (
                      <div 
                        key={game.id || index} 
                        className="game-history-item"
                        onClick={() => {
                          // Only allow replay if game_id exists (newer games with moves)
                          if (game.game_id) {
                            setSelectedReplay({
                              gameId: game.game_id,
                              opponent: game.opponent,
                              result: game.result,
                              playerColor: game.player_color,
                              timestamp: game.timestamp,
                              winType: game.win_type || null,
                              gameEndReason: game.game_end_reason || null
                            });
                          }
                        }}
                        style={{
                          ...(isModal ? {
                            background: game.result === 'win' ? '#e8f5e8' : 
                                       game.result === 'loss' ? '#f8d7da' : '#fff3cd',
                            border: `2px solid ${game.result === 'win' ? '#c3e6cb' : 
                                                 game.result === 'loss' ? '#f1b0b7' : '#ffeaa7'}`,
                            borderRadius: '6px',
                            padding: '5px 7px',
                            marginBottom: '5px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '5px'
                          } : {
                            background: game.result === 'win' ? '#e8f5e8' : 
                                       game.result === 'loss' ? '#f8d7da' : '#fff3cd',
                            border: `2px solid ${game.result === 'win' ? '#c3e6cb' : 
                                                 game.result === 'loss' ? '#f1b0b7' : '#ffeaa7'}`,
                            borderRadius: '8px',
                            padding: '12px 15px',
                            marginBottom: '10px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }),
                          cursor: game.game_id ? 'pointer' : 'default',
                          transition: 'all 0.2s',
                          ...(game.game_id ? {
                            ':hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                            }
                          } : {})
                        }}
                        onMouseEnter={(e) => {
                          if (game.game_id) {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = '';
                          e.currentTarget.style.boxShadow = '';
                        }}
                      >
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: isModal ? '3px' : '4px' }}>
                          {/* First line: Victory/Defeat, (Igo/Wego), and Opponent name */}
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center',
                            width: '100%',
                            gap: isModal ? '4px' : '5px',
                            flexWrap: 'wrap',
                            lineHeight: '1.2'
                          }}>
                            <div style={{ 
                              fontWeight: 'bold', 
                              color: game.result === 'win' ? '#155724' : 
                                     game.result === 'loss' ? '#721c24' : '#856404',
                              fontSize: isModal ? '0.85rem' : '1.05rem',
                              whiteSpace: 'nowrap',
                              flexShrink: 0
                            }}>
                              {game.result === 'win' ? '🏆 Victory' : 
                               game.result === 'loss' ? '💔 Defeat' : '🤝 Draw'}
                            </div>
                            {game.win_type && (game.result === 'win' || game.result === 'loss') && (
                              <span style={{ 
                                fontSize: isModal ? '0.75rem' : '0.95rem',
                                fontWeight: 'normal',
                                color: game.win_type === 'igo' ? '#e74c3c' : '#9b59b6',
                                whiteSpace: 'nowrap',
                                flexShrink: 0
                              }}>
                                ({game.win_type === 'igo' ? 'Igo' : 'Wego'})
                              </span>
                            )}
                            <span style={{ 
                              fontSize: isModal ? '0.72rem' : '0.85rem',
                              color: '#444',
                              fontWeight: '500',
                              whiteSpace: 'nowrap'
                            }}>
                              vs {game.opponent}
                            </span>
                            {game.game_mode === 'tournament' && (
                              <span
                                className="game-history-tournament-badge"
                                style={{
                                  fontSize: isModal ? '0.65rem' : '0.72rem',
                                  fontWeight: 700,
                                  letterSpacing: '0.03em',
                                  textTransform: 'uppercase',
                                  background: '#7d5a00',
                                  color: '#f9e79f',
                                  borderRadius: '999px',
                                  padding: isModal ? '1px 6px' : '2px 8px',
                                  whiteSpace: 'nowrap',
                                  flexShrink: 0
                                }}
                              >
                                Tournament
                              </span>
                            )}
                          </div>
                          {/* Second line: Played as White/Black */}
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%'
                          }}>
                            <div style={{ 
                              fontSize: isModal ? '0.75rem' : '0.9rem', 
                              color: '#666',
                              textAlign: 'center'
                            }}>
                              Played as {game.player_color === 'white' ? '⚪ White' : '⚫ Black'}
                            </div>
                          </div>
                        </div>
                        {/* Replay button */}
                        <div style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          marginLeft: isModal ? '5px' : '12px'
                        }}>
                          {game.game_id && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent triggering the parent onClick
                                setSelectedReplay({
                                  gameId: game.game_id!,
                                  opponent: game.opponent,
                                  result: game.result,
                                  playerColor: game.player_color,
                                  timestamp: game.timestamp,
                                  winType: game.win_type || null,
                                  gameEndReason: game.game_end_reason || null
                                });
                              }}
                              style={{
                                background: 'linear-gradient(45deg, #0f4ea5, #2c5282)',
                                color: 'white',
                                border: 'none',
                                borderRadius: isModal ? '4px' : '6px',
                                padding: isModal ? '3px 8px' : '6px 14px',
                                fontSize: isModal ? '0.7rem' : '0.85rem',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 4px rgba(15, 78, 165, 0.3)',
                                whiteSpace: 'nowrap'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 4px 8px rgba(15, 78, 165, 0.4)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = '';
                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(15, 78, 165, 0.3)';
                              }}
                            >
                              ▶️ Replay
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                    ) : (
                      <div style={{
                        textAlign: 'center',
                        padding: isModal ? '20px 10px' : '30px 20px',
                        color: '#999',
                        fontSize: isModal ? '0.85rem' : '1rem'
                      }}>
                        <p>🔍 No matches found for selected filters</p>
                        <p style={{ marginTop: '8px', fontSize: isModal ? '0.75rem' : '0.9rem' }}>
                          Try adjusting your date or tournament filters.
                        </p>
                      </div>
                    )
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: isModal ? '20px 10px' : '30px 20px',
                      color: '#999',
                      fontSize: isModal ? '0.85rem' : '1rem'
                    }}>
                      <p>🎮 No games recorded yet!</p>
                      <p style={{ marginTop: '8px', fontSize: isModal ? '0.75rem' : '0.9rem' }}>
                        Start playing to see your match history here.
                      </p>
                    </div>
                  )}
                  </div>
                </div>

            </>
          ) : (
            <div className="no-stats">
              <p>🎮 No statistics yet!</p>
              <p>Start playing to build your statistics!</p>
            </div>
          )}
        </div>

        {/* Copyright Footer */}
        {!isModal && (
          <div style={{
            padding: '20px',
            paddingLeft: '7.5%',
            textAlign: 'left',
            color: '#666',
            fontSize: '14px',
            marginTop: 'auto'
          }}>
            © 2025 Migoyugo. All rights reserved.
          </div>
        )}
      </div>

      {/* Game Replay Modal */}
      {selectedReplay && (
        <GameReplay
          gameId={selectedReplay.gameId}
          onClose={() => setSelectedReplay(null)}
          opponent={selectedReplay.opponent}
          result={selectedReplay.result}
          playerColor={selectedReplay.playerColor}
          timestamp={selectedReplay.timestamp}
          winType={selectedReplay.winType}
          gameEndReason={selectedReplay.gameEndReason}
        />
      )}
    </div>
  );
};

export default StatisticsPage;
